import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { UserMinus } from "lucide-react";
import { Modal } from "../../../ui/components/Modal";
import { Button } from "../../../ui/components/Button";
import { Avatar } from "../../../ui/components/Avatar";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { ListParams } from "../../../ui/api/listApi.types";
import {
  updateOrganizationMemberRole,
  type OrganizationMember,
  type OrganizationScopedRole,
} from "../../lib/api/users";
import { normalizeAvatarUrl } from "../../lib/profile-avatar-image";
import * as confirmStyles from "./OrganizationDetailPage.css";
import * as styles from "./RoleMembersModal.css";

const SOLE_ADMIN_TOOLTIP =
  "This user cannot change roles until at least one other member has the administrator organization role.";

export interface RoleMembersModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  orgId: string;
  accessToken: string;
  role: OrganizationScopedRole;
  members: OrganizationMember[];
  /** Target role id when removing (system Member). */
  memberRoleId: string | null;
  activeAdminCount: number;
  onMemberUpdated: (userId: string) => void;
}

type RoleMemberRow = OrganizationMember & { id: string };

function displayName(member: OrganizationMember): string {
  return (
    [member.firstName, member.lastName].filter(Boolean).join(" ").trim() ||
    member.email
  );
}

function isAdminMember(
  member: OrganizationMember,
  role: OrganizationScopedRole,
): boolean {
  return (
    role.systemKey === "admin" ||
    member.roleSystemKey === "admin" ||
    member.roleName === "admin" ||
    member.roleName === "pro:admin"
  );
}

export function RoleMembersModal({
  open,
  setOpen,
  orgId,
  accessToken,
  role,
  members,
  memberRoleId,
  activeAdminCount,
  onMemberUpdated,
}: RoleMembersModalProps) {
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const roleMembers = React.useMemo(
    () =>
      members
        .filter(
          (m) =>
            m.membershipStatus === "active" &&
            m.roleId === role.id,
        )
        .map((m) => ({ ...m, id: m.userId })),
    [members, role.id],
  );

  const canRemoveRoles =
    Boolean(memberRoleId) &&
    role.systemKey !== "member" &&
    memberRoleId !== role.id;

  React.useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  const fetchList = React.useCallback(
    (params: ListParams) => {
      const q = params.search?.toLowerCase() ?? "";
      const filtered = q
        ? roleMembers.filter((m) => {
            const name = displayName(m).toLowerCase();
            return name.includes(q) || m.email.toLowerCase().includes(q);
          })
        : roleMembers;
      const start = (params.page - 1) * params.limit;
      return Promise.resolve({
        data: filtered.slice(start, start + params.limit),
        total: filtered.length,
      });
    },
    [roleMembers],
  );

  const handleRemoveRole = async (member: RoleMemberRow) => {
    if (!memberRoleId || !canRemoveRoles) return;
    if (isAdminMember(member, role) && activeAdminCount <= 1) {
      setError(SOLE_ADMIN_TOOLTIP);
      return;
    }
    const name = displayName(member);
    const confirmed = window.confirm(
      `Remove the ${role.label?.trim() || role.name} role from ${name}? They will be assigned the Member role.`,
    );
    if (!confirmed) return;

    setActionLoading(true);
    setError(null);
    try {
      await updateOrganizationMemberRole(
        accessToken,
        orgId,
        member.userId,
        memberRoleId,
      );
      onMemberUpdated(member.userId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update member role");
    } finally {
      setActionLoading(false);
    }
  };

  const roleLabel = role.label?.trim() || role.name;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      minWidth="480px"
      maxWidth="560px"
      minHeight="auto"
    >
      <div className={confirmStyles.confirmModalBody}>
        <h3 className={confirmStyles.confirmModalTitle}>
          Members with {roleLabel}
        </h3>
        <p className={confirmStyles.confirmModalText}>
          Active organization members currently assigned this role.
        </p>
        {error && <Callout variant="error">{error}</Callout>}

        <ListView<RoleMemberRow>
          listId={`role-members-${orgId}-${role.id}`}
          contextKey={`${roleMembers.map((m) => m.userId).join(",")}-${roleMembers.length}`}
          queryOptions={{ staleTime: Infinity }}
          columns={[
            {
              id: "name",
              label: "Member",
              render: (row) => displayName(row),
            },
          ]}
          fetchList={fetchList}
          enabledViews={["stack"]}
          compact
          enableSelection={false}
          searchPlaceholder="Search members…"
          emptyTitle="No members have this role."
          getRowLabel={(row) => displayName(row)}
          onRowClick={(row) => {
            setOpen(false);
            void navigate({
              to: "/workspace/users/$userId",
              params: { userId: row.userId },
            });
          }}
          renderStackCard={(row, ctx) => {
            const soleAdmin =
              isAdminMember(row, role) && activeAdminCount <= 1;
            const name = displayName(row);
            return (
              <div
                key={row.id}
                className={styles.memberRow}
                role="button"
                tabIndex={0}
                onClick={ctx.onClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    ctx.onClick();
                  }
                }}
              >
                <Avatar
                  email={row.email}
                  firstName={row.firstName}
                  lastName={row.lastName}
                  image={normalizeAvatarUrl(row.avatarUrl) || undefined}
                  size={40}
                />
                <div className={styles.memberInfo}>
                  <p className={styles.memberName}>{name}</p>
                  <p className={styles.memberEmail}>{row.email}</p>
                </div>
                {canRemoveRoles ? (
                  <button
                    type="button"
                    className={styles.removeBtn}
                    disabled={actionLoading || soleAdmin}
                    title={soleAdmin ? SOLE_ADMIN_TOOLTIP : "Remove role"}
                    aria-label={`Remove role from ${name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleRemoveRole(row);
                    }}
                  >
                    <UserMinus size={16} aria-hidden />
                  </button>
                ) : null}
              </div>
            );
          }}
        />

        <div className={confirmStyles.confirmModalActions}>
          <Button
            color="neutral"
            variant="outline"
            size="md"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
